import { type Dispatch, useCallback, useEffect, useReducer, useRef } from "react";
import type { ProfileQuery, UserSegmentationData } from "@medbillai/graphql-types";
import { authStorage } from "../storage";
export interface Profile {
  name?: string;
  email: string;
}
export interface User {
  id: string;
  isAdmin: boolean;
  isShadow: boolean | undefined;
  adminUserId?: string;
  profile?: Profile;
  userSegmentationData?: UserSegmentationData;
}
export interface AuthState<TUser extends User = User> {
  user: TUser | null;
  isAuthenticated: boolean;
  error: Error | null;
  // Set to true once the initial auth state has been loaded from storage.
  // At this stage, we have not confirmed the validity of the session token.
  // This is used internally to know when to fetch the user's profile.
  isHydrated: boolean;
  // Set to true once the auth state has fully loaded, including retrieving
  // state from storage, and validating the session token. Note that this
  // requires a roundtrip to the API service, so there may be a short delay
  // at startup.
  isReady: boolean;
  // Used for Stytch magic login PKCE flow - this is a secret value
  // that we pass across a couple of requests.
  codeVerifier: string | null;
  // Last time a user was active on the app before it was pushed in the background.
  lastActiveTime: number | null;
  // Set to true once the user has finished the onboarding flow.
  // TODO consider moving this into the flow control logic and persisting there
  // with zustand middleware
  hasFinishedOnboarding: boolean;
  // The path the user was trying to access before being redirected to login
  intendedPath: string | null;
}
export const initialAuthState: AuthState<User> = {
  user: null,
  isAuthenticated: false,
  error: null,
  isHydrated: false,
  isReady: false,
  codeVerifier: null,
  lastActiveTime: null,
  hasFinishedOnboarding: false,
  intendedPath: null
};
export type Action<TUser extends User = User> = {
  type: "STORAGE_REHYDRATED";
  user: TUser | null;
  isAuthenticated: boolean;
  codeVerifier: string | null;
  lastActiveTime: number | null;
  hasFinishedOnboarding: boolean;
  intendedPath: string | null;
} | {
  type: "PROFILE_LOADED";
  profile: NonNullable<ProfileQuery["me"]>;
} | {
  type: "AUTH_READY";
} | {
  type: "LOGIN_REQUESTED";
} | {
  type: "LOGIN_CANCELED";
} | {
  type: "LOGIN_SUCCEEDED";
  user: TUser | null;
} | {
  type: "SHADOW_LOGIN";
  user: TUser | null;
} | {
  type: "LOGOUT";
} | {
  type: "REFRESH_FAILED";
} | {
  type: "ERROR";
  error: Error;
} | {
  type: "CODE_VERIFIER";
  codeVerifier: string | null;
} | {
  type: "FINISHED_ONBOARDING";
  hasFinishedOnboarding: boolean;
} | {
  type: "SET_INTENDED_PATH";
  intendedPath: string;
} | {
  type: "CLEAR_INTENDED_PATH";
};
export const reducer = <TUser extends User = User,>(state: AuthState<TUser>, action: Action<TUser>): AuthState<TUser> => {
  switch (action.type) {
    case "STORAGE_REHYDRATED":
      return {
        ...state,
        isHydrated: true,
        user: action.user,
        isAuthenticated: action.isAuthenticated,
        codeVerifier: action.codeVerifier,
        lastActiveTime: action.lastActiveTime,
        hasFinishedOnboarding: action.hasFinishedOnboarding,
        intendedPath: action.intendedPath
      };
    case "PROFILE_LOADED":
      {
        const profileData = action.profile;
        return {
          ...state,
          user: state.user && {
            ...state.user,
            isAdmin: profileData.isAdmin,
            userSegmentationData: profileData.segmentationData,
            profile: {
              name: profileData.profile.name ?? undefined,
              email: profileData.primaryEmail ?? ""
            }
          }
        };
      }
    case "AUTH_READY":
      return {
        ...state,
        isReady: true
      };
    case "LOGIN_REQUESTED":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        lastActiveTime: Date.now(),
        error: null
      };
    case "LOGIN_CANCELED":
      return {
        ...state
      };
    case "LOGIN_SUCCEEDED":
      return {
        ...state,
        user: action.user,
        isAuthenticated: !!action.user,
        error: null,
        codeVerifier: null
      };
    case "SHADOW_LOGIN":
      return {
        ...state,
        user: action.user
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        error: null,
        // Ensure storage-persisted state is reset.
        lastActiveTime: null,
        hasFinishedOnboarding: false,
        intendedPath: null
      };
    case "REFRESH_FAILED":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        error: null
      };
    case "ERROR":
      return {
        ...state,
        isReady: true,
        error: action.error
      };
    case "CODE_VERIFIER":
      return {
        ...state,
        codeVerifier: action.codeVerifier
      };
    case "FINISHED_ONBOARDING":
      return {
        ...state,
        hasFinishedOnboarding: action.hasFinishedOnboarding
      };
    case "SET_INTENDED_PATH":
      return {
        ...state,
        intendedPath: action.intendedPath
      };
    case "CLEAR_INTENDED_PATH":
      return {
        ...state,
        intendedPath: null
      };
    default:
      return state;
  }
};
export const usePersistedAuthReducer = <TUser extends User = User,>(initialState: AuthState<TUser>): {
  state: AuthState<TUser>;
  dispatch: Dispatch<Action<TUser>>;
  initialize: () => Promise<void>;
} => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current) {
      return;
    }
    void authStorage.set("isAuthenticated", JSON.stringify(state.isAuthenticated));
  }, [state.isAuthenticated]);
  useEffect(() => {
    if (!initialized.current) {
      return;
    }
    if (state.user) {
      void authStorage.set("user", JSON.stringify(state.user));
    } else {
      void authStorage.remove("user");
    }
  }, [state.user]);
  useEffect(() => {
    if (!initialized.current) {
      return;
    }
    if (state.codeVerifier) {
      void authStorage.set("codeVerifier", state.codeVerifier);
    } else {
      void authStorage.remove("codeVerifier");
    }
  }, [state.codeVerifier]);
  useEffect(() => {
    if (!initialized.current) {
      return;
    }
    if (state.lastActiveTime) {
      void authStorage.set("lastActiveTime", state.lastActiveTime.toString());
    } else {
      void authStorage.remove("lastActiveTime");
    }
  }, [state.lastActiveTime]);
  useEffect(() => {
    if (!initialized.current) {
      return;
    }
    void authStorage.set("hasFinishedOnboarding", state.hasFinishedOnboarding ? "true" : "false");
  }, [state.hasFinishedOnboarding]);
  useEffect(() => {
    if (!initialized.current) {
      return;
    }
    if (state.intendedPath) {
      void authStorage.set("intendedPath", state.intendedPath);
    } else {
      void authStorage.remove("intendedPath");
    }
  }, [state.intendedPath]);
  const initialize = useCallback(async () => {
    if (initialized.current) {
      return;
    }
    let isAuthenticated = (await authStorage.get("isAuthenticated")) === "true";
    let user = JSON.parse((await authStorage.get("user")) || "null") as TUser | null || null;
    const codeVerifier = (await authStorage.get("codeVerifier")) ?? null;
    if (user && !isAuthenticated) {
      user = null;
      await authStorage.remove("user");
    }
    if (isAuthenticated && !user) {
      isAuthenticated = false;
      await authStorage.set("isAuthenticated", "false");
    }
    // Make sure we don't load nonsensical state here for a logged-out user.
    const lastActiveTime = isAuthenticated ? (await authStorage.get("lastActiveTime")) ?? null : null;
    const hasFinishedOnboarding = isAuthenticated && (await authStorage.get("hasFinishedOnboarding")) === "true";

    // Load intended path from storage (it can persist across logout/login cycles)
    const intendedPath = (await authStorage.get("intendedPath")) ?? null;
    dispatch({
      type: "STORAGE_REHYDRATED",
      isAuthenticated,
      user,
      codeVerifier,
      lastActiveTime: lastActiveTime ? parseInt(lastActiveTime, 10) : null,
      hasFinishedOnboarding,
      intendedPath
    });
    initialized.current = true;
  }, []);
  return {
    state,
    dispatch,
    initialize
  };
};